import { create } from 'zustand'

export type ThemePreference = 'system' | 'light' | 'dark'

const STORAGE_KEY = 'catgo.theme'

function read(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored
  } catch {
    /* private mode */
  }
  return 'system'
}

function apply(preference: ThemePreference) {
  const root = document.documentElement
  if (preference === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', preference)

  // Keep the browser chrome in step with the ground the app is painting.
  const dark =
    preference === 'dark' ||
    (preference === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => meta.setAttribute('content', dark ? '#1c1333' : '#ffe9d6'))
}

interface ThemeStore {
  preference: ThemePreference
  set: (preference: ThemePreference) => void
}

export const useTheme = create<ThemeStore>((set) => ({
  preference: read(),
  set: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      /* private mode */
    }
    apply(preference)
    set({ preference })
  },
}))

/** Called once at start-up, before React paints. */
export function initTheme() {
  apply(read())
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => apply(useTheme.getState().preference))
}

export function isDarkNow(): boolean {
  const preference = useTheme.getState().preference
  if (preference === 'dark') return true
  if (preference === 'light') return false
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}
